import { useEffect, useState, useCallback } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
  usePersonalLoan,
  useOffer,
  usePollingTimeout,
  usePersonalLoanRouteFsm,
  useProfile,
  useTelemetry,
  useCrossSell,
  useLoader,
  useTimeout,
} from 'shared/hooks';
import {
  calculateMaximumAmount,
  calculateMinimumAmount,
  getLoanData,
} from 'shared/utils/offer-select-calculations';
import * as UwDecision from 'shared/constants/uw-decision';
import * as Consent from 'shared/constants/consent';
import * as CrossSellOfferPackage from 'shared/constants/cross-sell-offer-package';
import * as CrossSellAppStatus from 'shared/constants/cross-sell-app-status';
import PersonalLoanStateMachine, { ErrorStateMap } from 'state-machines/PersonalLoanStateMachine';
import { OfferSelectV2 } from './OfferSelectV2';

const OfferSelectContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const { telemetry } = useTelemetry();
  const { uuid: personalLoanUuid } = useParams();
  
  const {
    personalLoan,
    loading: personalLoanLoading,
    getPersonalLoanConsentTypes,
    updatePersonalLoan,
  } = usePersonalLoan();

  const { offerPackage, getOfferPackage, loading: offerPackageLoading } = useOffer();
  const { profile } = useProfile();
  const [uwFired, setUwFired] = useState(false);
  const runRouter = usePersonalLoanRouteFsm();
  const {
    crossSellOfferPackages,
    getCrossSellOfferPackages,
    loading: crossSellOfferPackagesLoading,
  } = useCrossSell();
  const [unloading, setUnloading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const crossSellApps = personalLoan.status?.crossSellAppStatus ?? [];
  const hasCrossSellApps = crossSellApps.length > 0;
  const hasActiveCrossSellApps =
    crossSellApps?.filter(
      (app) =>
        app.crossSellStatus !== CrossSellAppStatus.crossSellStatus.DECLINED &&
        app.crossSellStatus !== CrossSellAppStatus.crossSellStatus.CANCELLED
    ).length > 0 ?? false;
  const hasCrossSellOfferPackages = crossSellOfferPackages.length > 0;
  const helocOfferPackage = crossSellOfferPackages.find(
    (offerPackage) =>
      offerPackage.decision !== CrossSellOfferPackage.decision.DECLINE &&
      offerPackage.decision !== CrossSellOfferPackage.decision.ASSET_DEPLETION &&
      offerPackage.productType === CrossSellOfferPackage.productType.HELOC
  );
  const isHomeowner = personalLoan.currentLivingArrangement === 'HOMEOWNER';
  const waitForCrossSell = isHomeowner && hasCrossSellApps && !hasCrossSellOfferPackages;
  const isLoading =
    personalLoanLoading || offerPackageLoading || crossSellOfferPackagesLoading || waitForCrossSell;

  const hasOfferPackageDecision = offerPackage?.decision;
  const isCrossSellEligible = personalLoan.crossSellEligible;
  const shouldDisplayLoader =
    !hasOfferPackageDecision ||
    (isCrossSellEligible && !hasCrossSellApps) ||
    crossSellOfferPackagesLoading;
  const hasSignedAppSummary = personalLoan.status?.disclosurelistSummaryComplete;

  useLoader('Loading offers', shouldDisplayLoader);

  useTimeout(() => {
    if (shouldDisplayLoader)
      history.replace(`/applications/${personalLoanUuid}/review-app/timeout`);
  }, 20000);

  useEffect(() => {
    if (offerPackage?.offersByType?.every((o) => o.offers?.length === 0))
      history.replace(`/applications/${personalLoanUuid}/review-app/app-review`);
  }, [history, offerPackage.offersByType, personalLoanUuid]);

  useEffect(() => setUnloading(true), []);

  useEffect(() => {
    if (hasCrossSellApps) {
      getCrossSellOfferPackages(personalLoanUuid);
    }
  }, [hasCrossSellApps, getCrossSellOfferPackages, personalLoanUuid]);

  useEffect(() => {
    const shouldRedirect = !location.state?.bypassCrossSellRedirect;
    const lienValidationRequired = helocOfferPackage?.lienValidationRequired ?? true;

    if (
      !hasSignedAppSummary &&
      shouldRedirect &&
      hasActiveCrossSellApps &&
      !lienValidationRequired
    ) {
      history.push(`/applications/${personalLoanUuid}/multi-product`);
    }
  }, [
    helocOfferPackage,
    hasActiveCrossSellApps,
    history,
    personalLoanUuid,
    location,
    hasSignedAppSummary,
  ]);

  usePollingTimeout((poll) => {
    if (offerPackage?.decision || unloading) return;

    getOfferPackage(personalLoanUuid)
      .then(({ decision }) => {
        if (decision === UwDecision.REVIEW)
          history.replace(`/applications/${personalLoanUuid}/review-app/uw`);

        return;
      })
      .catch(() => poll());
  }, 10);

  useEffect(() => {
    if (!personalLoan.status || !personalLoan.uuid || !profile.uuid) return;

    const errorStateValues = Object.values(ErrorStateMap);
    const fsmState = PersonalLoanStateMachine.run(profile, personalLoan);

    // user has selected an offer, hard credit has been pulled.  Need to move forward and not allow the user to select another offer
    const ivHardCredPulled =
      personalLoan.status?.uwSubDecision === 'VERIFIED_INCOME_MATCHED_OFFER' &&
      personalLoan.status?.uwDecision === UwDecision.FINAL;

    if ((!isRedirecting && errorStateValues.includes(fsmState)) || ivHardCredPulled) {
      setIsRedirecting(true);
      runRouter();
      return;
    }
  }, [personalLoan, profile, runRouter, isRedirecting]);

  useEffect(() => {
    if (offerPackage?.offersByType?.some((o) => o.offers?.length > 0) && !uwFired) {
      setUwFired(true);
      const fixedOffers = offerPackage.offersByType
        .find((offer) => offer.rateType === 'FIXED')
        .offers.sort((a, b) => a.baseRate - b.baseRate);
      const minOffer = fixedOffers[0];
      const maxOffer = fixedOffers[fixedOffers.length - 1];
      const minAmount = calculateMinimumAmount(fixedOffers);
      const maxAmount = calculateMaximumAmount(fixedOffers);
      const { apr: low_rate } = getLoanData(
        minAmount,
        minOffer.baseRate,
        minOffer.term,
        minOffer.originationFeePercentage
      );
      const { apr: max_rate } = getLoanData(
        maxAmount,
        maxOffer.baseRate,
        maxOffer.term,
        maxOffer.originationFeePercentage
      );

      telemetry('plUwPass', {
        eventProperties: {
          low_rate: parseFloat(low_rate),
          max_rate: parseFloat(max_rate),
          max_amount: Math.floor(offerPackage.max),
          low_rate_term: minOffer.term,
        },
      });
    }
  }, [offerPackage, telemetry, uwFired]);

  const handleSubmit = useCallback(
    async (data) => {
      const consents = await getPersonalLoanConsentTypes();
      await updatePersonalLoan(personalLoanUuid, {
        ...personalLoan,
        consentsList: [
          ...personalLoan.consentsList,
          {
            type: Consent.CREDIT_UNION_MEMBERSHIP,
            version: consents.find((c) => c.type === Consent.CREDIT_UNION_MEMBERSHIP).version,
            optInOut: Consent.OPT_IN,
            creditUnion: offerPackage.creditUnion,
          },
        ],
        payType: data.payType,
        selectedOffer: {
          amount: data.amount,
          rate: data.rate,
          term: data.term,
          rateType: data.rateType,
          originationFeePercentage: Number(data.originationFeePercentage),
          selectedDiscounts: data.selectedDiscounts,
          aprRate: data.aprRate,
          estimatedMonthlyPayment: data.estimatedMonthlyPayment,
        },
      });
      telemetry('plOfferSelected', {
        eventProperties: {
          selected_rate: data.rate,
          selected_rate_type: data.rateType,
          selected_term: data.term,
          selected_amount: data.amount,
        },
      });

      const route = location.state?.from;

      if (route) {
        history.push(route);
      } else {
        runRouter();
      }
    },
    [
      location.state?.from,
      offerPackage.creditUnion,
      getPersonalLoanConsentTypes,
      personalLoan,
      personalLoanUuid,
      history,
      telemetry,
      runRouter,
      updatePersonalLoan,
    ]
  );

  if (isLoading || shouldDisplayLoader) return null;
  if (typeof offerPackage.offersByType !== 'object' || offerPackage.offersByType.length === 0)
    return null;

  return (
    <>
      <OfferSelectV2
        offerPackage={offerPackage}
        personalLoan={personalLoan}
        handleSubmit={handleSubmit}
        profile={profile}
        hasSignedAppSummary={hasSignedAppSummary}
        helocOfferPackage={helocOfferPackage}
        plOfferSelected={location.state?.plOfferSelected || false}
      />
    </>
  );
};

export default OfferSelectContainer;
